"use client";

import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { AppLocale } from "@/core/i18n/config";
import { normalizeAppError } from "@/shared/lib/async-result";
import { createSlotHold, getAvailableSlots } from "../../../data/repositories/booking.repository";
import type { AvailableDay, AvailableSlot, AvailableSlotsResponse } from "../../../types/booking";
import { useBookingStepperStore } from "../../states/booking-stepper.store";
import { GROUP_LABEL } from "./shared";
type DateTab = "today" | "tomorrow" | "thisWeek" | "nextWeek";
function getErrorMessage(error: unknown): string {
  return normalizeAppError(error).message;
}
function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}
function diffInCalendarDays(dateString: string, today: Date): number {
  const target = startOfDay(new Date(`${dateString}T00:00:00`));
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}
function slotGroupsForDay(day: AvailableDay) {
  return (["morning", "afternoon", "evening"] as const).map(key => ({
    key,
    slots: day.slots[key]
  })).filter(group => group.slots.length > 0);
}
export function Step3DateTime({
  healerId,
  locale
}: {
  healerId: string;
  locale: AppLocale;
}) {
  const isHindi = locale === "hi";
  const selectedDurationMinutes = useBookingStepperStore(state => state.selectedDurationMinutes);
  const selectedMode = useBookingStepperStore(state_0 => state_0.selectedMode);
  const setSlotHold = useBookingStepperStore(state_1 => state_1.setSlotHold);
  const previousStep = useBookingStepperStore(state_2 => state_2.previousStep);
  const [data, setData] = useState<AvailableSlotsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DateTab>("today");
  const [isHolding, setIsHolding] = useState(false);
  const [holdError, setHoldError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getAvailableSlots(healerId, selectedDurationMinutes ?? undefined);
        if (!cancelled) setData(response);
      } catch (caughtError) {
        if (!cancelled) setError(getErrorMessage(caughtError));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [healerId, selectedDurationMinutes]);
  const today = useMemo(() => startOfDay(new Date()), []);
  const days = data?.days ?? [];
  const todayDay = days.find(day => day.displayDate === "Today");
  const tomorrowDay = days.find(day_0 => day_0.displayDate === "Tomorrow");
  const thisWeekDays = days.filter(day_1 => {
    const diff = diffInCalendarDays(day_1.date, today);
    return diff >= 0 && diff <= 6;
  });
  const nextWeekDays = days.filter(day_2 => {
    const diff_0 = diffInCalendarDays(day_2.date, today);
    return diff_0 >= 7 && diff_0 <= 13;
  });
  const visibleDaysByTab: Record<DateTab, AvailableDay[]> = {
    today: todayDay ? [todayDay] : [],
    tomorrow: tomorrowDay ? [tomorrowDay] : [],
    thisWeek: thisWeekDays,
    nextWeek: nextWeekDays
  };
  const activeDays = visibleDaysByTab[activeTab];
  async function handleSlotClick(slot: AvailableSlot) {
    if (!selectedMode || !selectedDurationMinutes || isHolding) return;
    setIsHolding(true);
    setHoldError(null);
    try {
      const hold = await createSlotHold(healerId, {
        startDatetime: slot.start,
        mode: selectedMode,
        durationMinutes: selectedDurationMinutes
      });
      setSlotHold({
        slot,
        holdId: hold.id,
        holdExpiresAt: hold.expiresAt
      });
    } catch (caughtError_0) {
      const message = getErrorMessage(caughtError_0);
      setHoldError(message);
      toast.error(message);
    } finally {
      setIsHolding(false);
    }
  }
  const tabs: {
    key: DateTab;
    en: string;
    hi: string;
  }[] = [{
    key: "today",
    en: "Today",
    hi: "आज"
  }, {
    key: "tomorrow",
    en: "Tomorrow",
    hi: "कल"
  }, {
    key: "thisWeek",
    en: "This Week",
    hi: "इस सप्ताह"
  }, {
    key: "nextWeek",
    en: "Next Week",
    hi: "अगले सप्ताह"
  }];
  return <div>
      <h2 className="font-heading text-xl font-semibold text-[#0E2A3A]">
        {isHindi ? "तिथि और समय चुनें" : "Choose a date and time"}
      </h2>

      {loading && <div className="mt-8 flex items-center justify-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          {isHindi ? "स्लॉट लोड हो रहे हैं..." : "Loading slots..."}
        </div>}

      {!loading && error && <p className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}

      {!loading && !error && <>
          <div className="mt-5 flex flex-wrap gap-1 rounded-full bg-[#FAF8F5] p-1">
            {tabs.map(tab => <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)} className={`min-w-[70px] flex-1 rounded-full px-2.5 py-2 text-xs font-semibold transition ${activeTab === tab.key ? "bg-[#155A7D] text-white shadow-sm" : "text-slate-500 hover:text-[#155A7D]"}`}>
                {isHindi ? tab.hi : tab.en}
              </button>)}
          </div>

          <div className="mt-5 space-y-5">
            {activeDays.length === 0 ? <p className="text-sm text-slate-500">
                {isHindi ? "इस अवधि के लिए कोई स्लॉट उपलब्ध नहीं है।" : "No slots available for this period."}
              </p> : activeDays.map(day_3 => {
          const groups = slotGroupsForDay(day_3);
          return <div key={day_3.date}>
                    {(activeTab === "thisWeek" || activeTab === "nextWeek") && <p className="text-xs font-semibold tracking-wide text-slate-400 uppercase">
                        {day_3.displayDate}
                      </p>}
                    <div className="mt-2 space-y-3">
                      {groups.map(group => <div key={group.key}>
                          <p className="text-xs font-medium text-slate-400">
                            {isHindi ? GROUP_LABEL[group.key].hi : GROUP_LABEL[group.key].en}
                          </p>
                          <div className="mt-1.5 flex flex-wrap gap-2">
                            {group.slots.map(slot_0 => <button key={slot_0.start} type="button" disabled={isHolding} onClick={() => handleSlotClick(slot_0)} className="rounded-full border border-[#EAE5DA] px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-[#155A7D]/50 hover:text-[#155A7D] disabled:opacity-50">
                                {slot_0.displayTime}
                              </button>)}
                          </div>
                        </div>)}
                    </div>
                  </div>;
        })}
          </div>

          {holdError && <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{holdError}</p>}
        </>}

      <div className="mt-8 flex gap-3">
        <button type="button" onClick={previousStep} className="rounded-full border border-[#EAE5DA] px-5 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">
          {isHindi ? "वापस" : "Back"}
        </button>
      </div>
    </div>;
}